import React, { useEffect, useRef, useState } from 'react';
import { Input } from './ui/input';
import { MapPin } from 'lucide-react';

export const AddressAutocomplete = ({ value, onChange, required = false, placeholder = "Enter service address" }) => {
  const containerRef = useRef(null);
  const autocompleteElementRef = useRef(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    // Check if Google Maps is loaded
    const checkGoogleLoaded = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsGoogleLoaded(true);
        return true;
      }
      return false;
    };

    if (checkGoogleLoaded()) return;

    // Poll for Google Maps to load
    const interval = setInterval(() => {
      if (checkGoogleLoaded()) {
        clearInterval(interval);
      }
    }, 100);

    // Timeout after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!isGoogleLoaded) {
        setApiError('Google Maps failed to load. Please check the API key configuration.');
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isGoogleLoaded]);

  useEffect(() => {
    if (!isGoogleLoaded || !containerRef.current || autocompleteElementRef.current) return;

    try {
      // Check if PlaceAutocompleteElement is available (new API)
      if (window.google.maps.places.PlaceAutocompleteElement) {
        // Create the new PlaceAutocompleteElement
        const autocomplete = new window.google.maps.places.PlaceAutocompleteElement({
          componentRestrictions: { country: 'us' },
        });

        // Style the element
        autocomplete.style.cssText = `
          width: 100%;
          height: 44px;
          border: 2px solid #e5e7eb;
          border-radius: 6px;
          padding-left: 40px;
          font-size: 14px;
          outline: none;
          box-sizing: border-box;
        `;

        // Add focus styling
        autocomplete.addEventListener('focus', () => {
          autocomplete.style.borderColor = '#22c55e';
        });
        autocomplete.addEventListener('blur', () => {
          autocomplete.style.borderColor = '#e5e7eb';
        });

        // Listen for place selection using the new API
        autocomplete.addEventListener('gmp-select', async (event) => {
          try {
            const place = event.placePrediction.toPlace();
            await place.fetchFields({ fields: ['formattedAddress', 'addressComponents'] });
            if (place.formattedAddress) {
              onChange(place.formattedAddress);
            }
          } catch (err) {
            console.error('Error fetching place details:', err);
          }
        });

        // Clear the container and append the element
        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(autocomplete);
        autocompleteElementRef.current = autocomplete;
      } 
      // Fallback to legacy Autocomplete if PlaceAutocompleteElement is not available
      else if (window.google.maps.places.Autocomplete) {
        // Create an input element for legacy autocomplete
        const input = document.createElement('input');
        input.type = 'text';
        input.placeholder = placeholder;
        input.value = value || '';
        input.style.cssText = `
          width: 100%;
          height: 44px;
          border: 2px solid #e5e7eb;
          border-radius: 6px;
          padding-left: 40px;
          font-size: 14px;
          outline: none;
          box-sizing: border-box;
        `;
        input.addEventListener('focus', () => {
          input.style.borderColor = '#22c55e';
        });
        input.addEventListener('blur', () => {
          input.style.borderColor = '#e5e7eb';
        });

        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(input);

        const autocomplete = new window.google.maps.places.Autocomplete(input, {
          types: ['address'],
          componentRestrictions: { country: 'us' },
          fields: ['formatted_address', 'address_components']
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place && place.formatted_address) {
            onChange(place.formatted_address);
          }
        });

        // Handle manual input
        input.addEventListener('input', (e) => {
          onChange(e.target.value);
        });

        autocompleteElementRef.current = { input, autocomplete };
      } else {
        setApiError('Google Places API is not available. Please enable the Places API in Google Cloud Console.');
      }
    } catch (err) {
      console.error('Error initializing autocomplete:', err);
      setApiError('Failed to initialize address autocomplete. Please try again later.');
    }
  }, [isGoogleLoaded, onChange, placeholder, value]);

  // Fallback to regular input if there's an API error or Google hasn't loaded
  if (apiError || !isGoogleLoaded) {
    return (
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10">
          <MapPin className="w-5 h-5" />
        </div>
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className="border-2 focus:border-green-500 pl-10"
        />
        {apiError && (
          <p className="text-xs text-amber-600 mt-1">{apiError}</p>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10 pointer-events-none">
        <MapPin className="w-5 h-5" />
      </div>
      <div ref={containerRef} className="autocomplete-container" />
    </div>
  );
};
